// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TerceraWindowBase } from './TerceraWindowBase';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import WebhooksScreenTemplate from '@front/templates/WebhooksScreenTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { WebhookTableItem } from '../cache/WebhookTableItem';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { Control } from '../elements/Control';
import { WebhooksManager } from '@shared/commons/cache/Webhooks/WebhooksManager';
import { Connection } from '@shared/commons/Connection';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { EventCache } from '@shared/commons/cache/Event/EventCache';
import { EventType } from '@shared/commons/cache/Event/EventConstants';
import { Event } from '@shared/commons/cache/Event/Event';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { ThemeManager } from '../misc/ThemeManager';
import { SvgIconType } from '@components/webdesktop/elements/SvgIcon/SvgIconType';
import { ButtonType } from '@components/webdesktop/elements/Button/ButtonType';
import { BrandingUtils } from '@shared/commons/UtilsClasses/BrandingUtils';
export class WebhooksScreen extends TerceraWindowBase {
    constructor() {
        super();
        this.quickTableRactive = null;
        this.webhooksManager = new WebhooksManager(Connection);
        this.onTableRowButtonClicked = (args) => __awaiter(this, void 0, void 0, function* () {
            const item = args.row.item;
            if (args.realColumnIndex !== WebhookTableItem.DELETE_COL_INDEX) {
                return false;
            }
            const result = yield this.webhooksManager.removeWebhookAsync(item.webhook);
            if (result) {
                this.createRemoveEvent(item.webhook);
                args.table.RemoveItem(item.ItemId);
            }
            return result;
        });
        this.onCreateKeyButtonClicked = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.set('createKeyButtonIsDisabled', true);
            const name = this.get('createKeyInputValue');
            const webhook = yield this.webhooksManager.createWebhookAsync(isValidString(name) ? name : null);
            if (!webhook) {
                this.set('createKeyButtonIsDisabled', false);
                return;
            }
            this.createAddEvent(webhook);
            (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable.AddItem(new WebhookTableItem(webhook, SessionSettings));
            const webhookCreatedScreen = MainWindowManager.Factory.addScreen(ScreensNames.WebhookCreatedScreen);
            webhookCreatedScreen.set('webhook', webhook);
            webhookCreatedScreen.on('teardown', () => {
                this.set('createKeyButtonIsDisabled', false);
            });
        });
    }
    getType() {
        return ScreensNames.WebhooksScreen;
    }
    oncomplete() {
        this.initializeTable();
        this.localize();
        void this.layoutTable();
        Control.Ticker.Subscribe(this.TickAsync, this);
        ThemeManager.onThemeChange.Subscribe(this.themeChange, this);
        super.oncomplete();
        this.addScrollToContainer();
        void this.populateAndShowAsync();
        this.themeChange();
    }
    dispose() {
        const quickTableRactive = this.quickTableRactive;
        if (!isNullOrUndefined(quickTableRactive)) {
            const quickTable = quickTableRactive.quickTable;
            quickTable.OnDisplayModeChanged.UnSubscribe(this.onDisplayModeChanged, this);
            quickTable.OnAddItem.UnSubscribe(this.onAddItem, this);
            quickTable.OnDeleteItem.UnSubscribe(this.onDeleteItem, this);
            quickTable.OnPaintedPictureButtonClick.UnSubscribe(this.onTableRowButtonClicked, this);
        }
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        ThemeManager.onThemeChange.UnSubscribe(this.themeChange, this);
        super.dispose();
    }
    TickAsync() {
        var _a;
        const quickTable = (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable;
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        quickTable.Draw();
        quickTable.needRedraw = false;
        quickTable.needRedrawBackground = true;
    }
    localize() {
        var _a;
        void this.set({
            header: Resources.getResource('screen.Webhooks'),
            secretKeyTitle: Resources.getResource('screen.Webhooks.secretKeyInfo.Title'),
            secretKeyDescription: Resources.getResource('screen.Webhooks.secretKeyInfo.Description'),
            createKeyInputPlaceholder: Resources.getResource('screen.Webhooks.keyNameInput.placeholder'),
            createKeyButtonText: Resources.getResource('screen.Webhooks.createKeyButton.text'),
            createKeyButtonTooltip: Resources.getResource('screen.Webhooks.createKeyButton.tooltip'),
            secretKeyTip: Resources.getResource('screen.Webhooks.tip'),
            viewApiDocsText: Resources.getResource('screen.Webhooks.viewApiDocs')
        });
        (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable.localize();
    }
    initializeTable() {
        this.quickTableRactive = this.findComponent('quickTableRactive');
        const quickTable = this.quickTableRactive.quickTable;
        quickTable.allowGroupBy = false;
        quickTable.canShowHeaderMenu = false;
        quickTable.CustomTextAlign = 'center';
        quickTable.InitializeDirect(new WebhookTableItem(null, SessionSettings));
        quickTable.UpdateSortedColumns();
        quickTable.OnDisplayModeChanged.Subscribe(this.onDisplayModeChanged, this);
        quickTable.OnAddItem.Subscribe(this.onAddItem, this);
        quickTable.OnDeleteItem.Subscribe(this.onDeleteItem, this);
        quickTable.OnPaintedPictureButtonClick.Subscribe(this.onTableRowButtonClicked, this);
    }
    layoutTable() {
        return __awaiter(this, void 0, void 0, function* () {
            const quickTableRactive = this.quickTableRactive;
            if (isNullOrUndefined(quickTableRactive)) {
                return;
            }
            const quickTable = quickTableRactive.quickTable;
            const rowHeight = quickTable.rowHeight;
            const visibleRowsCount = Math.max(WebhooksScreen.MIN_VISIBLE_ROWS_COUNT, Math.min(WebhooksScreen.MAX_VISIBLE_ROWS_COUNT, quickTable.rowsArray.length));
            const tableHeight = rowHeight * (visibleRowsCount + 1);
            yield quickTableRactive.resetSizes();
            quickTableRactive.setSizes(null, tableHeight);
        });
    }
    themeChange() {
        var _a;
        (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.themeChange();
    }
    populateAndShowAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.populateTableAsync();
            yield this.set('visible', true);
            this.center();
        });
    }
    populateTableAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const webhooks = yield this.webhooksManager.getWebhooksAsync();
            const quickTable = (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable;
            if (isNullOrUndefined(quickTable)) {
                return;
            }
            for (const webhook of webhooks) {
                quickTable.AddItem(new WebhookTableItem(webhook, SessionSettings));
            }
        });
    }
    onDisplayModeChanged() {
        void this.layoutTable();
    }
    onAddItem(_row) {
        void this.layoutTable();
    }
    onDeleteItem(_id) {
        void this.layoutTable();
    }
    createAddEvent(webhook) {
        const ev = new Event({
            DateTime: DateTimeUtils.DateTimeUtcNow(),
            Action: 'event.Created.Webhook',
            Description: Resources.getResource('event.Created.Description', [webhook.name]),
            EventType: EventType.System
        });
        EventCache.Add(ev);
    }
    createRemoveEvent(webhook) {
        const ev = new Event({
            DateTime: DateTimeUtils.DateTimeUtcNow(),
            Action: 'event.Removed.Webhook',
            Description: Resources.getResource('event.Removed.Description', [webhook.name]),
            EventType: EventType.System
        });
        EventCache.Add(ev);
    }
}
WebhooksScreen.MIN_VISIBLE_ROWS_COUNT = 3;
WebhooksScreen.MAX_VISIBLE_ROWS_COUNT = 3;
TerceraWindowBase.extendWith(WebhooksScreen, {
    data() {
        return {
            movable: true,
            resizable: false,
            showFooter: false,
            focused: true,
            width: 490,
            visible: false,
            secretKeyTitle: '',
            secretKeyDescription: '',
            createKeyInputValue: '',
            createKeyInputPlaceholder: '',
            createKeyButtonType: ButtonType.Primary,
            createKeyButtonIcon: SvgIconType.Add,
            createKeyButtonText: '',
            createKeyButtonTooltip: '',
            createKeyButtonIsDisabled: false,
            secretKeyTip: '',
            webhookBrandingUrl: BrandingUtils.GetBrandingWebhookURL(),
            viewApiDocsText: '',
            viewApiDocsUrl: BrandingUtils.GetBrandingWebhookHelpURL()
        };
    },
    partials: { bodyPartial: WebhooksScreenTemplate }
});
